import { ErrorModal } from '@modusign/design-system';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import {
  changePaymentNotification,
  fetchPaymentNotification,
  getPaymentNotificationContact,
} from '@app/features/payment';
import useDeposit from '@app/features/payment/hooks/useDeposit';
import { PAYMENT_ERROR_MESSAGE_MAPPER } from '@app/features/subscription/constants';
import { createSubscription as createSubscriptionThunk } from '@app/features/subscription/state';
import PaymentItem from '@app/features/subscription/types/PaymentItem';
import { PaymentItemType } from '@app/features/subscription/types/PaymentItemType';
import PaymentResultState from '@app/features/subscription/types/PaymentResultState';
import PaymentType from '@app/features/subscription/types/PaymentType';
import { PlanInterval } from '@app/features/subscription/types/PlanInterval';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import { recordGAEvent } from '@app/store/analytics/actions';
import { Dispatch } from '@app/store/types';

function VirtualAccountCreateSuccessPage() {
  const dispatch = useDispatch<Dispatch>();
  const history = useHistory<PaymentResultState>();
  const { search } = useLocation();
  const workspaceId = useWorkspaceId();

  const notificationContact = useSelector(getPaymentNotificationContact);

  useDeposit();

  const [paymentError, setPaymentError] = useState<Error | null>(null);

  const query = new URLSearchParams(search);
  const impSuccess = query.get('imp_success') === 'true';
  const amount = Number(query.get('amount')) || 0;
  const contact = query.get('contact') || '';
  const expireDay = query.get('expire_day') || '';
  const errorMessage = query.get('error_msg') || '';
  const planId = query.get('plan_id') || '';
  const planIntervalQuery = query.get('plan_interval') || '';
  const planTitle = query.get('plan_title') || '';
  const type = query.get('type') || '';

  const planInterval = (() => {
    const q = planIntervalQuery.toLowerCase();
    switch (q) {
      case 'month':
        return PlanInterval.MONTH;
      case 'year':
        return PlanInterval.YEAR;
      case 'two-years':
        return PlanInterval.TWO_YEARS;
      case 'three-years':
        return PlanInterval.THREE_YEARS;
      default:
        throw new Error('Unsupported interval.');
    }
  })();

  const paymentItems: PaymentItem[] = useMemo(() => {
    const paymentItemsValue: PaymentItem[] = [];
    if (planId) {
      paymentItemsValue.push({ planId, type: PaymentItemType.PLAN });
    }
    return paymentItemsValue;
  }, [planId]);

  const handlePaymentErrorModalConfirmClick = () => {
    history.replace({
      pathname: type === 'CHARGE_DEPOSIT' ? '/settings/billing' : '/settings/plan',
    });
  };

  const activateSubscription = useCallback(async () => {
    if (!contact) {
      return;
    }

    if (contact !== notificationContact) {
      const resultAction = await dispatch(changePaymentNotification({ contact, type: 'EMAIL' }));
      if (changePaymentNotification.rejected.match(resultAction)) {
        setPaymentError({
          name: '요금제 결제 실패',
          message: resultAction.error.message || '메시지 없음',
        });
        return;
      }
    }

    history.replace({
      pathname: '/settings/plan',
      state: {
        paymentType: PaymentType.CREATE_VIRTUAL_ACCOUNT,
        virtualAccountAmount: amount,
        virtualAccountExpiryDate: moment(expireDay).toISOString(),
      },
    });
  }, [amount, contact, dispatch, expireDay, history, notificationContact]);

  const chargeDeposit = useCallback(() => {
    history.replace({
      pathname: '/settings/billing',
      state: {
        paymentType: PaymentType.CREATE_VIRTUAL_ACCOUNT,
        virtualAccountAmount: amount,
        virtualAccountExpiryDate: moment(expireDay).toISOString(),
      },
    });
  }, [amount, expireDay, history]);

  const createSubscription = useCallback(async () => {
    if (!contact || !planInterval || !planTitle) {
      return;
    }

    if (contact !== notificationContact) {
      const resultAction = await dispatch(changePaymentNotification({ contact, type: 'EMAIL' }));
      if (changePaymentNotification.rejected.match(resultAction)) {
        setPaymentError({
          name: '요금제 결제 실패',
          message: resultAction.error.message || '메시지 없음',
        });
        return;
      }
    }

    const resultAction = await dispatch(
      createSubscriptionThunk({
        activation: false,
        items: paymentItems,
      })
    );

    if (createSubscriptionThunk.rejected.match(resultAction)) {
      setPaymentError({
        name: '요금제 결제 실패',
        message: resultAction.error.message || '메시지 없음',
      });
      return;
    }

    dispatch(
      recordGAEvent(
        '결제',
        '요금제_결제완료',
        `(${(() => {
          switch (planInterval) {
            case PlanInterval.MONTH:
              return '월';
            case PlanInterval.YEAR:
              return '연';
            case PlanInterval.TWO_YEARS:
              return '2년';
            case PlanInterval.THREE_YEARS:
              return '3년';
            default:
              return `${planInterval}개월`;
          }
        })()} 결제)${planTitle.toUpperCase()}(${'예치금'})`,
        0
      )
    );

    history.replace({
      pathname: '/settings/plan',
      state: {
        paymentType: PaymentType.CREATE_VIRTUAL_ACCOUNT,
        virtualAccountAmount: amount,
        virtualAccountExpiryDate: moment(expireDay).toISOString(),
      },
    });
  }, [
    amount,
    contact,
    dispatch,
    expireDay,
    history,
    notificationContact,
    paymentItems,
    planInterval,
    planTitle,
  ]);

  useEffect(() => {
    if (workspaceId) {
      dispatch(fetchPaymentNotification());
    }
  }, [dispatch, workspaceId]);

  useEffect(() => {
    if (!notificationContact) {
      return;
    }

    if (impSuccess) {
      if (type === 'CREATE_SUBSCRIPTION') {
        createSubscription();
      } else if (type === 'ACTIVATE_SUBSCRIPTION') {
        activateSubscription();
      } else if (type === 'CHARGE_DEPOSIT') {
        chargeDeposit();
      }
    } else {
      setPaymentError({
        name: '가상계좌 발급 실패',
        message: errorMessage,
      });
    }
  }, [
    activateSubscription,
    chargeDeposit,
    createSubscription,
    errorMessage,
    impSuccess,
    notificationContact,
    type,
  ]);

  if (paymentError) {
    return (
      <ErrorModal
        error={paymentError.message}
        errorMessageMapper={PAYMENT_ERROR_MESSAGE_MAPPER}
        isOpen
        title={paymentError.name}
        onClose={handlePaymentErrorModalConfirmClick}
      />
    );
  }

  return null;
}

export default VirtualAccountCreateSuccessPage;
